import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TopicItem } from './TopicItem';
import { TopicHeader } from './TopicHeader';
import { TopicSkeleton } from './TopicSkeleton';
import { MobileDrawer } from '../mobile/MobileDrawer';
import { Brain, MessageSquare, Sparkles } from 'lucide-react';
import { useTopicSkeletonState } from '../../hooks/useTopicSkeletonState';
import { logger } from '../../utils/logger';
import { useTopicList } from '../../contexts/TopicListContext';
import { EmptyState } from './EmptyState';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
// Note: Realtime subscriptions are now handled by useProjectTopicSubscription
import { useTopicUIStore } from '../../store/topicUIStore';
import { useTopicFetching } from '../../hooks/useTopicFetching';
import { useProjectUIStore } from '../../store/projectUIStore';
import { useProjectTopicSubscription } from '../../hooks/useTopicSubscription';
import { useInitialLoadingScreenManager } from '../../hooks/useInitialLoadingScreenManager';

interface TopicListProps {
  onTopicSelect: (topicId: string) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  onNewTopic?: () => void;
  forceLoading?: boolean; // External loading state can be passed from parent components
  projectId?: string; // Optional projectId for cases when it's passed from parent
}

export function TopicList({
  onTopicSelect,
  isMobileMenuOpen = false,
  setIsMobileMenuOpen = () => {},
  onNewTopic,
  forceLoading = false,
  projectId,
}: TopicListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isListHovered, setIsListHovered] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const { activeTopicId } = useTopicUIStore(); // Using UI store for active topic
  const { activeProjectId: storeProjectId } = useProjectUIStore();

  // Use provided projectId or fall back to store
  const effectiveProjectId = projectId || storeProjectId;

  // Use the enhanced topic fetching hook that handles validation and stale data
  const {
    topics,
    loading: topicsLoading,
    fetching: topicsFetching,
  } = useTopicFetching(effectiveProjectId);

  // Use the unified skeleton state hook with an override for forcing loading
  const { loading: skeletonLoading } = useTopicSkeletonState(effectiveProjectId);

  // Create a merged loading state
  const loading = skeletonLoading || forceLoading || topicsLoading;

  // Set up real-time subscription for topics in the current project
  useProjectTopicSubscription(effectiveProjectId);

  // Get collapseTopicList from context
  const { collapseTopicList } = useTopicList();

  // Use context's collapseTopicList function
  const handleCollapse = React.useCallback(() => {
    logger.component('TopicList: Handling collapse with context function');
    collapseTopicList();
  }, [collapseTopicList]);

  // Memoize filtered topics to prevent unnecessary re-renders
  const filteredTopics = useMemo(() => {
    // When loading, return an empty array to avoid flashing empty state
    if (loading) return [];

    // When there are no topics, still process this normally so empty state can show
    const query = searchQuery.toLowerCase();
    if (!query) return topics;

    return topics.filter(
      topic =>
        topic.name.toLowerCase().includes(query) || topic.description?.toLowerCase().includes(query)
    );
  }, [topics, searchQuery, loading]);

  // Memoize topic list to prevent re-renders
  const topicList = useMemo(
    () => (
      <div className="divide-y divide-transparent">
        {filteredTopics.map(topic => (
          <TopicItem
            key={topic.id}
            topic={topic}
            isActive={topic.id === activeTopicId}
            isListHovered={isListHovered}
            onClick={() => onTopicSelect(topic.id)}
          />
        ))}
      </div>
    ),
    [filteredTopics, activeTopicId, onTopicSelect, isListHovered]
  );

  // Get the viewport context to check for mobile
  const { isMobile } = useViewport();

  // Get status bar information for mobile adjustments
  const { isNativeApp, navigationBarHeight } = useStatusBarInfo();

  // Get loading screen manager
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Mobile-specific loading screen dismissal
  // On mobile, the topic list is shown first without a topic selected,
  // so we need to dismiss the loading screen when the topic list loads
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (isMobile && !loading && !hasMarkedCompleteRef.current) {
      logger.component('TopicList: Mobile view - marking route complete as topic list is loaded');
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [isMobile, loading, markRouteComplete]);

  // Log status bar info for debugging
  React.useEffect(() => {
    if (isNativeApp && isMobile) {
      logger.capacitor('TopicList: Using navigation bar height', {
        navigationBarHeight,
      });
    }
  }, [isNativeApp, navigationBarHeight, isMobile]);

  // Add scroll detection for mobile header shadow
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      if (listContainerRef.current) {
        const scrollTop = listContainerRef.current.scrollTop;
        setIsScrolled(scrollTop > 5); // Smaller threshold for lighter shadow
      }
    };

    const scrollContainer = listContainerRef.current;
    if (scrollContainer) {
      // Initial check
      handleScroll();

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile]);

  // Simplified touch handling for swipe - detect but don't animate
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    // Only use if we're on mobile
    if (!isMobile) return;

    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
  };

  // Handle touch move - detect only, no animation
  const handleTouchMove = (e: React.TouchEvent) => {
    // Only process if we have a valid start position
    if (touchStartX === null || !isMobile) return;

    const touch = e.touches[0];
    const swipeDistance = touch.clientX - touchStartX;

    // Only track rightward swipes (from left edge to right)
    if (swipeDistance > 0) {
      // Threshold detection without animation
      if (swipeDistance > 50) {
        setTouchStartX(null); // Reset immediately
        setIsMobileMenuOpen(true);
      }
    }
  };

  // Simple cleanup
  const handleTouchEnd = () => {
    setTouchStartX(null);
  };

  // Define the empty state content
  const emptyState = useMemo(() => {
    // If we're on mobile and there are no topics, we want to show the nice empty state
    // but only when not searching
    const showMobileEmptyState = isMobile && topics.length === 0 && !searchQuery;

    return (
      <>
        {/* Desktop empty state - minimal version */}
        <div className="hidden lg:flex flex-col items-center justify-center h-full text-center">
          <div className="mb-4">
            <MessageSquare className="w-12 h-12 text-gray-400" />
          </div>
          <p className="text-gray-500">
            {searchQuery ? 'No topics match your search criteria' : 'No topics yet'}
          </p>
        </div>

        {/* Mobile empty state */}
        {showMobileEmptyState ? (
          <div className="mt-16">
            <EmptyState onCreateTopic={onNewTopic} hasTopics={false} variant="topicList" />
          </div>
        ) : (
          <div className="lg:hidden h-full flex items-center justify-center p-8">
            <div className="w-full max-w-sm mx-auto text-center">
              <div className="relative mb-8">
                {/* Main icon */}
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-105">
                  <Brain className="w-10 h-10 text-white" />
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center transform rotate-12">
                  <Sparkles className="w-4 h-4 text-yellow-600" />
                </div>
                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center transform -rotate-12">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-sora">
                {topics.length > 0 ? 'No Topics Found' : 'Start Your First Topic'}
              </h2>

              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                {searchQuery
                  ? 'No topics match your search criteria'
                  : 'Create your first topic to start organizing your project.'}
              </p>

              <button
                onClick={onNewTopic}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl px-6 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:translate-y-[-2px]"
              >
                <MessageSquare className="w-6 h-6" />
                <span className="text-lg font-semibold">New Topic</span>
              </button>
            </div>
          </div>
        )}
      </>
    );
  }, [searchQuery, topics.length, onNewTopic, isMobile]);

  if (!effectiveProjectId) {
    return null;
  }

  // Instead of hiding the header entirely, we'll just hide the search and new topic button
  // when we're in the mobile empty state
  const shouldHideSearchAndNewTopic = isMobile && topics.length === 0 && !loading && !searchQuery;

  // Add a loading indicator when in a stale-while-revalidate state
  // (data is available but a background update is happening)
  const isBackgroundFetching = !topicsLoading && topicsFetching;

  return (
    <>
      <div
        className="flex flex-col h-full relative topic-list-container z-[40] rounded-none overflow-hidden"
        onMouseEnter={() => setIsListHovered(true)}
        onMouseLeave={() => setIsListHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-hovered={isListHovered ? 'true' : 'false'}
        data-loading={loading ? 'true' : 'false'}
        data-fetching={isBackgroundFetching ? 'true' : 'false'}
        data-empty={!loading && topics.length === 0 ? 'true' : 'false'}
      >
        {/* Always show header, but optionally hide search and new topic button */}
        <TopicHeader
          projectId={effectiveProjectId}
          onNewTopic={onNewTopic}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onMenuClick={() => setIsMobileMenuOpen(true)}
          onCollapse={handleCollapse}
          hideSearchAndNewTopic={shouldHideSearchAndNewTopic}
          isScrolled={isScrolled}
          isBackgroundFetching={isBackgroundFetching}
        />

        <div
          ref={listContainerRef}
          className="flex-1 overflow-y-auto overflow-x-hidden relative z-[40]"
          style={{
            height:
              isNativeApp && isMobile
                ? `calc(100vh - 120px - ${navigationBarHeight}px)`
                : 'calc(100vh - 120px)',
            maxHeight:
              isNativeApp && isMobile
                ? `calc(100vh - 120px - ${navigationBarHeight}px)`
                : 'calc(100vh - 120px)',
            overflowY: 'auto',
            paddingBottom: isNativeApp && isMobile ? `${navigationBarHeight}px` : '0',
          }}
        >
          {loading ? <TopicSkeleton /> : filteredTopics.length === 0 ? emptyState : topicList}
        </div>

        <MobileDrawer
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          projectId={effectiveProjectId}
        />
      </div>
    </>
  );
}
